import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { throttle } from "../../../../../utils/function"
import {
  clearAuthSession,
  getAuthSession,
  loginAccount,
  registerAccount
} from "../../../auth-session"
import { joinLobbyRoom } from "../../../game/lobby-logic"
import { useAppDispatch, useAppSelector } from "../../../hooks"
import { fetchProfile } from "../../../network"
import { logIn, logOut } from "../../../stores/NetworkStore"
import "./login.css"

export default function Login() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const uid = useAppSelector((state) => state.network.uid)
  const displayName = useAppSelector((state) => state.network.displayName)
  const email = useAppSelector((state) => state.network.email)
  const [prejoining, setPrejoining] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)
  const [mode, setMode] = useState<"login" | "register">("login")
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [nickname, setNickname] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const preJoinLobby = throttle(async function prejoin() {
    setPrejoining(true)
    return joinLobbyRoom(dispatch, navigate)
      .then(() => navigate("/lobby"))
      .catch(() => setPrejoining(false))
  }, 1000)

  useEffect(() => {
    const session = getAuthSession()
    if (session) {
      dispatch(
        logIn({
          uid: session.uid,
          displayName: session.displayName,
          email: session.username
        })
      )
    }
  }, [dispatch])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const session =
        mode === "register"
          ? await registerAccount(username, password, nickname || username)
          : await loginAccount(username, password)
      dispatch(
        logIn({
          uid: session.uid,
          displayName: session.displayName,
          email: session.username
        })
      )
      await fetchProfile(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : "请求失败")
    } finally {
      setSubmitting(false)
    }
  }

  if (!uid) {
    return (
      <div className="login-panel">
        <header className="login-panel-header">
          <h2>{mode === "login" ? "欢迎回来" : "创建账号"}</h2>
          <p>
            {mode === "login"
              ? "登录后进入大厅，开始你的自走棋对局"
              : "注册一个新账号，马上开始游戏"}
          </p>
        </header>

        <div className="login-tabs" role="tablist" aria-label="登录方式">
          <button
            type="button"
            role="tab"
            aria-selected={mode === "login"}
            className={mode === "login" ? "active" : ""}
            onClick={() => {
              setMode("login")
              setError(null)
            }}
          >
            登录
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "register"}
            className={mode === "register" ? "active" : ""}
            onClick={() => {
              setMode("register")
              setError(null)
            }}
          >
            注册
          </button>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          <label className="login-field">
            <span>用户名</span>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              placeholder="3–20 位字母、数字或下划线"
              required
            />
          </label>
          <label className="login-field">
            <span>密码</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={
                mode === "register" ? "new-password" : "current-password"
              }
              placeholder="至少 6 位"
              required
            />
          </label>
          {mode === "register" && (
            <label className="login-field">
              <span>昵称（可选）</span>
              <input
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="游戏中显示的名字"
              />
            </label>
          )}
          {error && <p className="login-error">{error}</p>}
          <button className="login-submit" type="submit" disabled={submitting}>
            {submitting
              ? "请稍候..."
              : mode === "register"
                ? "注册并进入"
                : "登录"}
          </button>
        </form>
      </div>
    )
  }

  return (
    <div className="login-panel login-panel--logged-in">
      <header className="login-panel-header">
        <p className="login-welcome-label">当前账号</p>
        <h2 title={`${displayName}${email ? ` (${email})` : ""}`}>
          {displayName || t("auth.hover_to_reveal")}
        </h2>
        <p>已登录，可以进入大厅开始游戏</p>
      </header>

      <div className="login-actions">
        <button
          className="login-submit"
          onClick={preJoinLobby}
          disabled={prejoining}
        >
          {prejoining ? t("auth.connecting") : "进入大厅"}
        </button>
        <button
          className="login-secondary"
          disabled={prejoining || loggingOut}
          onClick={async () => {
            setLoggingOut(true)
            try {
              clearAuthSession()
              dispatch(logOut())
            } finally {
              setLoggingOut(false)
            }
          }}
        >
          {loggingOut ? t("auth.signing_out") : "退出登录"}
        </button>
      </div>
    </div>
  )
}
